import React, { useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Star, StarOutline } from '@mui/icons-material';

import { useCustomNavigate, useSearchReasons, useToggle, useURLParams, useUser } from '@dh/hooks';
import { Project } from '@dh/types';
import { isObjectEmpty } from '@dh/utils';
import { favourizeProject } from '@dh/api/users';

import { BoldedText } from '../bolded-text/BoldedText';
import { sortTagsBySearchRelevance } from '../tag-container/sortTagsBySearchRelevance';
import { ChipContainerProps } from '../tag-container/TagContainer';
import { ProjectTableRow, ProjectTitleSmall } from './styles';
import { IconButton } from '../buttons';

/* To avoid server from trying to access DOM element in component */
const TagContainer = dynamic<ChipContainerProps>(
    () => import('../tag-container/TagContainer').then((module) => module.TagContainer),
    { ssr: false }
);

type Props = {
    project: Project;
};

const ProjectRow = ({ project }: Props) => {
    const { title, topics } = project;
    const { user } = useUser();
    const { searchTerm, searchTopic } = useURLParams();
    const boldedTerms = useSearchReasons();
    const [isFavourite, toggleIsFavourite] = useToggle(true);
    const { navigateToProjectDetails } = useCustomNavigate();

    const handleStarClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            if (user) {
                toggleIsFavourite();
                void favourizeProject(user.id, project, !isFavourite);
            }
        },
        [isFavourite, project, toggleIsFavourite, user]
    );

    const usedTitle = useMemo(() => {
        if (searchTerm && searchTerm.splitTerm) {
            return (
                <BoldedText
                    fullString={title}
                    inBold={searchTerm.splitTerm}
                    style={{ fontWeight: 700 }}
                />
            );
        } else {
            return title;
        }
    }, [searchTerm, title]);

    const topicTags = useMemo(
        () =>
            searchTerm || searchTopic
                ? sortTagsBySearchRelevance(topics, searchTerm, searchTopic)
                : topics.map((t) => t.title),
        [searchTerm, searchTopic, topics]
    );

    return (
        <ProjectTableRow
            aria-label='project row'
            onClick={() => navigateToProjectDetails(project.documentId)}
        >
            <ProjectTitleSmall
                mr={0.5}
                style={{ fontWeight: !isObjectEmpty(searchTerm) ? 500 : 600 }}
            >
                {usedTitle}
            </ProjectTitleSmall>

            <TagContainer
                tags={topicTags.map((title) => ({
                    title
                }))}
                rows={1}
                boldedTerms={boldedTerms}
            />
            <IconButton
                icon={isFavourite ? <Star color={'primary'} /> : <StarOutline />}
                onClick={handleStarClick}
            />
        </ProjectTableRow>
    );
};
export { ProjectRow };
